/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IEventModel,
  ImageHelper,
  IMediaModel,
  RouteHelper,
} from "@bms/common";
import React from "react";
import { Link } from "react-router-dom";

import "./CompetitorRow.scss";

type ICompetitor = NonNullable<IEventModel["Competitors"]>[number];

export type IRow = ICompetitor & { DisplayPosition: number };

interface ICompetitorRowProps {
  row: IRow;
  hasPlayerTeam: boolean;
  hasResultTime: boolean;
  hasScore: boolean;
}

export const CompetitorRow = ({
  row,
  hasPlayerTeam,
  hasResultTime,
  hasScore,
}: ICompetitorRowProps) => {
  const primary = row.Player;

  const primaryName = [primary?.FirstName, primary?.LastName]
    .filter(Boolean)
    .join(" ");

  const primaryImage =
    ImageHelper.getSquareImage(primary?.Images) ??
    ImageHelper.getRoundImage(primary?.Images);

  const primaryFlag = primary?.CountryFlag;

  const primaryCountryName = primary?.CountryName;

  const primaryLink =
    primary?.MediaId && primary?.MediaTypeCode
      ? RouteHelper.getDetailsPath({
          MediaTypeCode: primary.MediaTypeCode,
          Id: primary.MediaId,
        } as IMediaModel)
      : undefined;

  const team = row.Team;

  const teamLink =
    team?.MediaId && team?.MediaTypeCode
      ? RouteHelper.getDetailsPath({
          MediaTypeCode: team.MediaTypeCode,
          Id: team.MediaId,
        } as IMediaModel)
      : undefined;

  return (
    <tr>
      <td className="CompetitorsTab__col--pos">{row.DisplayPosition}</td>
      <td className="CompetitorsTab__col--competitor">
        <div className="CompetitorsTab__competitor">
          {primaryImage && (
            <img
              src={primaryImage.Url}
              alt={primaryName}
              className="CompetitorsTab__competitor-avatar"
            />
          )}
          {primaryFlag && (
            <img
              src={primaryFlag}
              alt={primaryCountryName}
              className="CompetitorsTab__competitor-flag"
            />
          )}
          {primaryLink ? (
            <Link to={primaryLink} className="CompetitorsTab__competitor-link">
              {primaryName || "—"}
            </Link>
          ) : (
            <span>{primaryName || "—"}</span>
          )}
        </div>
      </td>
      {hasPlayerTeam && (
        <td className="CompetitorsTab__col--team">
          {teamLink ? (
            <Link to={teamLink} className="CompetitorsTab__team-link">
              {team?.Name ?? "—"}
            </Link>
          ) : (
            team?.Name ?? "—"
          )}
        </td>
      )}
      {hasResultTime && (
        <td className="CompetitorsTab__col--time">{row.ResultTime ?? "—"}</td>
      )}
      {hasScore && (
        <td className="CompetitorsTab__col--score">{row.Score ?? "—"}</td>
      )}
    </tr>
  );
};
